/**
 * @file SSOVendorEnvironmentDropdown.js
 * @description A dropdown component for selecting SSO vendor environments.
 */

import React, { useState, useEffect } from "react";
import { fetchSSOVendorEnvironments } from "../../../services/ssoService";
import { Dropdown } from "../../helpers/Dropdown";
import LoadingWrapper from "../../ui/loaders/LoadingWrapper";
import Toast from "../../ui/toasts/Toast";
import { getText } from '../../../i18n';

/** 
 * Represents a dropdown component for selecting SSO vendor environments.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - The CSS class name for the component.
 * @param {boolean} [props.inlineLabel=true] - Determines whether the label is displayed inline with the dropdown.
 * @param {string} [props.selectedEnvironment] - The ID of the selected SSO vendor environment.
 * @param {function} props.onEnvironmentChange - The callback function to be called when the selected environment changes.
 * @param {string} [props.label="SSO Vendor Environment"] - The label text for the dropdown.
 * @param {boolean} [props.isRequired=false] - Determines whether the dropdown is required.
 * @param {boolean} [props.allowNullSelection=false] - Determines whether a null selection is allowed.
 * @returns {JSX.Element} The rendered SSOVendorEnvironmentDropdown component.
 */
const SSOVendorEnvironmentDropdown = ({
  className,
  inlineLabel = true,
  selectedEnvironment,
  onEnvironmentChange,
  label = "SSO Vendor Environment",
  placeholder = "Select SSO Vendor Environment",
  isRequired = false,
  allowNullSelection = false,
}) => {
  const [environments, setEnvironments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const getEnvironments = async () => {
      setIsLoading(true);
      try {
        const data = await fetchSSOVendorEnvironments();
        setEnvironments(data);
      } catch (error) {
        console.error("Could not fetch SSO vendor environments:", error);
        Toast.error(getText('msg_49e6a8781f83'));
      } finally {
        setIsLoading(false);
      }
    };

    getEnvironments();
  }, []);

  const getDefaultLabel = () => {
    const matchingEnvironment = environments.find(
      (env) => env.id === selectedEnvironment
    );
    return matchingEnvironment ? matchingEnvironment.name : placeholder;
  };

  const environmentOptions = allowNullSelection
    ? [
        { value: null, label: placeholder },
        ...environments.map((env) => ({
          value: env.id,
          label: env.name,
        })),
      ]
    : environments.map((env) => ({
        value: env.id,
        label: env.name,
      }));

  return (
    <div
      className={`form-group mb-3 ${
        inlineLabel ? "inline-input" : ""
      } ${className}`}
    >
      <label htmlFor="sso-vendor-environment-dropdown">
        {label}
        {isRequired && <span className="text-danger">*</span>}
      </label>
      <LoadingWrapper isLoading={isLoading}>
        <Dropdown
          id="sso-vendor-environment-dropdown"
          defaultValue={getDefaultLabel()}
          options={environmentOptions}
          value={selectedEnvironment}
          onChange={onEnvironmentChange}
          disabled={environments.length === 0 || isLoading}
        />
      </LoadingWrapper>
    </div>
  );
};

export default SSOVendorEnvironmentDropdown;
